
import { useState, useMemo } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import HeroFAQ from './components/HeroFAQ';
import FAQCategories from './components/FAQCategories';
import FAQAccordion from './components/FAQAccordion';
import FAQContact from './components/FAQContact';
import { faqItems, faqCategories } from '../../mocks/faq';

const FAQPage = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredItems = useMemo(() => {
    let items = faqItems;

    if (activeCategory !== 'all') {
      items = items.filter((item) => item.category === activeCategory);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      items = items.filter(
        (item) =>
          item.question.toLowerCase().includes(q) ||
          item.answer.toLowerCase().includes(q)
      );
    }

    return items;
  }, [activeCategory, searchQuery]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    if (query.trim()) setActiveCategory('all');
  };

  const categoriesWithCount = useMemo(() => {
    return faqCategories.map((cat) => {
      if (cat.id === 'all') return { ...cat, count: faqItems.length };
      return {
        ...cat,
        count: faqItems.filter((item) => item.category === cat.id).length,
      };
    });
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <main>
        <HeroFAQ onSearch={handleSearch} />

        {/* Categories */}
        <section className="py-10 border-b border-slate-100 bg-white sticky top-20 z-40 shadow-sm">
          <div className="max-w-5xl mx-auto px-6 lg:px-8">
            <FAQCategories
              categories={categoriesWithCount}
              activeCategory={activeCategory}
              onSelect={(id) => {
                setActiveCategory(id);
                setSearchQuery('');
              }}
            />
          </div>
        </section>

        {/* FAQ List */}
        <section className="py-16">
          <div className="max-w-3xl mx-auto px-6 lg:px-8">
            {/* Results header */}
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2
                  className="text-xl font-bold text-slate-800"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {searchQuery
                    ? `Resultados para "${searchQuery}"`
                    : categoriesWithCount.find((c) => c.id === activeCategory)?.label ?? 'Todas as Perguntas'}
                </h2>
                <p className="text-slate-400 text-sm mt-0.5">
                  {filteredItems.length} {filteredItems.length === 1 ? 'pergunta encontrada' : 'perguntas encontradas'}
                </p>
              </div>
              {(searchQuery || activeCategory !== 'all') && (
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setActiveCategory('all');
                  }}
                  className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-800 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-close-circle-line text-base"></i>
                  Limpar filtros
                </button>
              )}
            </div>

            <FAQAccordion items={filteredItems} />
          </div>
        </section>

        {/* Stats Banner */}
        <section className="py-12 bg-slate-900">
          <div className="max-w-5xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              {[
                { icon: 'ri-star-fill', value: '4.9★', label: 'Avaliação média', color: 'text-amber-400' },
                { icon: 'ri-time-line', value: '< 1h', label: 'Tempo de resposta', color: 'text-emerald-400' },
                { icon: 'ri-home-4-line', value: '4', label: 'Apartamentos', color: 'text-sky-400' },
                { icon: 'ri-calendar-check-line', value: '365', label: 'Dias por ano', color: 'text-rose-400' },
              ].map((stat, i) => (
                <div key={i} className="flex flex-col items-center gap-2">
                  <div className={`w-10 h-10 flex items-center justify-center rounded-full bg-white/10 ${stat.color}`}>
                    <i className={`${stat.icon} text-lg`}></i>
                  </div>
                  <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
                  <p className="text-slate-400 text-xs">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <FAQContact />
      </main>

      <Footer />
    </div>
  );
};

export default FAQPage;
